import { useCallback, useMemo } from 'react'
import useGetFilters from '../../../../../../hooks/useGetAllTags'
import PremiumLocked from '../../../../../Items/Lock'
import CustomTour from '../../../../../CustomTour'
import Spinner from 'components/Spinner/index.js'
import { settings } from 'settings'
import { titleCase } from 'utils'
import Input from 'components/Input'

export const UnavailableFilterSpan = ({
  isAvailableInSolvingMode = () => {
    return false
  },
  top,
  left,
}) => {
  return (
    <span>
      {!isAvailableInSolvingMode() && (
        <PremiumLocked
          customMessage={
            'Filtrul este nu este disponibil în modul de rezolvare ales.'
          }
          lockStyle={{
            position: 'relative',
            top: top,
            left: left,
            width: '20px',
            height: '15px',
            color: 'grey',
            zIndex: '101',
          }}
        />
      )}
    </span>
  )
}

export const FilterDialog = ({
  hasSubscription,
  filterDifficulty,
  handleSelectDifficulty,
  handleTagsCheckboxChange,
  filteredTags,
  solvingModes,
  handleChangeSolvingMode,
  grileTypes,
  handleChangeGrileTypes,
  handleChangeChaptersByCourse,
  coursesWithChapters,
}) => {
  const lockedMessage = useMemo(
    () =>
      `Poți filtra doar după ${settings.materie} (${Object.keys(coursesWithChapters).join(', ')}) fără un abonament premium`,
    [coursesWithChapters],
  )

  // this should only be called for the difficulty filter
  const isAvailableInSolvingMode = useCallback(() => {
    // in any other solving mode than the first one, the difficulty needs to be disabled
    if (
      !solvingModes ||
      solvingModes.findIndex(mode => mode.checked === true) === 0
    ) {
      return true
    }

    return false
  }, [solvingModes])

  const mappedSolvingModes = useMemo(() => {
    return solvingModes?.map((mode, index) => {
      return (
        <label
          key={`solvingModes-${index}`}
          className="flex flex-row items-start my-1 transition-all md:items-center"
        >
          <Input
            type="radio"
            name="solvingMode"
            className="transition-all"
            checked={mode.checked}
            onChange={() => handleChangeSolvingMode(index)}
            isLocked={!hasSubscription}
            isLockedMessage={lockedMessage}
          />
          <div className="flex flex-col items-start justify-center md:flex-row md:items-center">
            <div className="text-sm mx-2">{mode.name}</div>{' '}
            <div className="hidden md:block">-</div>{' '}
            <div className="text-xs ml-2 opacity-50">{mode.description}</div>
          </div>
        </label>
      )
    })
  }, [solvingModes, hasSubscription, lockedMessage, handleChangeSolvingMode])

  const mappedChapters = useMemo(() => {
    const orderMaterii = settings.order_materii

    const sortedCourses = Object.entries(coursesWithChapters || {}).sort(
      (a, b) => {
        return orderMaterii.indexOf(a[0]) - orderMaterii.indexOf(b[0])
      },
    )

    if (
      sortedCourses === undefined ||
      sortedCourses === null ||
      sortedCourses.length === 0
    ) {
      return (
        <>
          <Spinner />
          <CustomTour
            isOpen={
              sortedCourses === undefined ||
              sortedCourses === null ||
              sortedCourses.length === 0
            }
          />
        </>
      )
    }

    return sortedCourses.map(([course, chapters]) => {
      const disabled =
        chapters.every(chapter => filteredTags?.includes(chapter.id)) &&
        filteredTags?.length === chapters.length
      const isSelected = chapters.every(chapter =>
        filteredTags?.includes(chapter.id),
      )

      return (
        <div key={course}>
          <label key={`label-${course}`} className="flex items-center gap-1">
            <Input
              type="checkbox"
              id={`course-${course}`}
              name={`course-${course}`}
              checked={isSelected}
              className="!w-[20px] !h-[20px] !min-w-[20px] !min-h-[20px] cursor-pointer transition-all"
              // disabled={disabled}
              onChange={() => handleChangeChaptersByCourse(course, isSelected)}
            />

            <div className="capitalize">{course}</div>
          </label>
          <div className="mt-[8px] mb-[9px] w-[55px] h-[0.2px] bg-gray-200"></div>
          {chapters.map((chapter, chapterIndex) => {
            const totalSelectedChapters = Object.values(
              coursesWithChapters || {},
            )
              .flat()
              .filter(chapter => filteredTags?.includes(chapter.id)).length

            const isOnlyOneSelected = totalSelectedChapters === 1

            // const isDisabled =
            //   !hasSubscription ||
            //   (isOnlyOneSelected && filteredTags?.includes(chapter.id))

            const isDisabled = !hasSubscription

            return (
              <label
                htmlFor={chapter?.id}
                key={`label-${chapterIndex}`}
                className="flex items-center gap-1 mt-[6px]"
              >
                <Input
                  className="!w-4 !h-4 !min-w-4 !min-h-4 cursor-pointer"
                  type="checkbox"
                  id={chapter?.id}
                  name={chapter?.id}
                  checked={filteredTags?.includes(chapter?.id)}
                  onChange={event => {
                    handleTagsCheckboxChange(event, chapter?.id)
                  }}
                  isLocked={isDisabled}
                  isLockedMessage={lockedMessage}
                />
                <div
                  className={`capitalize text-[9px] lg:text-xs text-[#B7B7B7]}`}
                >
                  {chapter?.name}
                </div>
              </label>
            )
          })}{' '}
        </div>
      )
    })
  }, [
    coursesWithChapters,
    filteredTags,
    handleChangeChaptersByCourse,
    hasSubscription,
    lockedMessage,
    handleTagsCheckboxChange,
  ])

  const mappedDifficulty = useMemo(() => {
    const isOnlyOneSelected =
      filterDifficulty?.filter(item => item.checked).length === 1

    return filterDifficulty?.map((difficulty, index) => {
      const isDisabled = isOnlyOneSelected && difficulty.checked

      return (
        <div key={`difficulty-${index}`} className="flex flex-col gap-2">
          <label
            htmlFor={difficulty?.key}
            key={difficulty?.key}
            className="flex items-center gap-1"
            disabled={!isAvailableInSolvingMode()}
          >
            <Input
              type="checkbox"
              id={difficulty?.key}
              name={difficulty?.key}
              className="!w-4 !h-4 !min-w-4 !min-h-4 cursor-pointer"
              disabled={
                !isAvailableInSolvingMode()
                // !hasSubscription || isDisabled || !isAvailableInSolvingMode()
              }
              checked={difficulty?.checked}
              onChange={() => handleSelectDifficulty(index)}
              isLocked={!hasSubscription}
              isLockedMessage={lockedMessage}
            />
            <div
              className={`${!isAvailableInSolvingMode() && 'text-[#B7B7B7]'}`}
            >
              {difficulty?.label}
            </div>
          </label>
        </div>
      )
    })
  }, [
    filterDifficulty,
    isAvailableInSolvingMode,
    hasSubscription,
    lockedMessage,
    handleSelectDifficulty,
  ])

  const mappedGrileTypes = useMemo(() => {
    return grileTypes?.map((type, index) => {
      const isDisabled = !hasSubscription

      // const isDisabled =
      //   (grileTypes?.filter(item => item.checked === true).length === 1 &&
      //     type.checked === true) ||
      //   !hasSubscription

      return (
        <label key={`grileType-${index}`} className="flex items-center gap-1">
          <Input
            type="checkbox"
            className="!w-4 !h-4 !min-w-4 !min-h-4 cursor-pointer"
            checked={type.checked}
            onChange={() => handleChangeGrileTypes(index)}
            isLocked={isDisabled}
            isLockedMessage={lockedMessage}
          />
          {type.name}
        </label>
      )
    })
  }, [grileTypes, hasSubscription, lockedMessage, handleChangeGrileTypes])

  return (
    <div className="filter-dialog-grile text-over-ternary-background sm:max-h-60vh md:max-h-70vh lg:max-h-80vh xl:max-h-90vh">
      {/* Solving Modes */}
      <h1 className="opacity-60 text-lg flex gap-2 items-center">
        <span>Mod rezolvare</span>
      </h1>
      <div className="mt-1">{mappedSolvingModes}</div>
      {/* Chapters */}
      <h1 className="opacity-60 text-lg mt-3">
        {titleCase(settings.materii)} / Capitole
      </h1>
      <div className={`mt-1 flex flex-row gap-5 w-full`}>{mappedChapters}</div>
      {/* Difficulties */}
      <div className="flex flex-row">
        <h1
          className={`opacity-60 text-lg mt-3 ${
            !isAvailableInSolvingMode() && 'opacity-30'
          }`}
        >
          Dificultate
        </h1>
        <UnavailableFilterSpan
          isAvailableInSolvingMode={isAvailableInSolvingMode}
          top="8px"
          left="2px"
        />
      </div>
      <div className="flex gap-3 items-center mt-1">{mappedDifficulty}</div>
      <div className="flex flex-row">
        <h1 className="opacity-60 text-lg mt-3">Tip</h1>
      </div>
      {/* Type */}
      <div className="flex gap-3 items-center mt-1 pb-4">
        {mappedGrileTypes}
      </div>
    </div>
  )
}
